import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { capitalize } from 'lodash-es';
import { useHistory } from 'react-router';
import { Button, Page, PageErrorState, Typography } from 'seagull';
import { routes } from '~/constants';
import { useIntegrationData } from '~/hooks/reactQuery/derived/useIntegrationData';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { isLinked } from '~/models/integration';
import { Tab } from '~/pages/NotificationSettings/NotificationSettings.utils';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import getManageIntegrationRoute from './ConnectedApps.utils';
import styles from './ConnectedAppsPage.module.scss';
export default function ConnectedAppsPage() {
    const { t: texts } = useLocaleKeys('pages.connectedAppsPage');
    const isVendor = useStore(selectors.user.isVendor);
    const { data: integrations, error, refresh: refreshIntegrations } = useIntegrationData({
        enabled: isVendor
    });
    const history = useHistory();
    const onBack = ()=>{
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push(routes.availableApps);
        }
    };
    const onExploreApps = ()=>{
        history.push(routes.availableApps);
    };
    const sortedAndFilteredIntegrations = useMemo(()=>integrations ? [
            ...integrations
        ].sort((a, b)=>a.source.localeCompare(b.source)).filter((integration)=>isLinked(integration)) : [], [
        integrations
    ]);
    const onManageIntegration = (integration)=>{
        if (integration.source === 'gmail' || integration.source === 'outlook') {
            history.push(routes.notifications, {
                selectedTab: Tab.Client
            });
            return;
        }
        history.push(getManageIntegrationRoute(integration));
    };
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: "integrations-error-state",
            title: 'Error loading integrations',
            onRefresh: ()=>refreshIntegrations({
                    throwOnError: false
                })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.connectedAppsWrapper,
        children: /*#__PURE__*/ _jsx(Page, {
            header: texts.header(),
            subheader: texts.subheader(),
            onClickBack: onBack,
            testId: "connected-apps-page",
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.integrationsContainer,
                children: sortedAndFilteredIntegrations.length > 0 ? sortedAndFilteredIntegrations.map((integration)=>/*#__PURE__*/ _jsxs("div", {
                        className: styles.integration,
                        "data-testid": "integration-".concat(integration.source),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                className: styles.integrationHeader,
                                children: integration.source === 'qbo' ? 'Quickbooks Online' : capitalize(integration.source)
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'manage_connected_app',
                                    data: {
                                        source: integration.source
                                    }
                                },
                                testId: "manage-integration-button-".concat(integration.source),
                                size: "medium",
                                intent: Intent.ConnectIntegration,
                                onClick: ()=>onManageIntegration(integration),
                                className: styles.manageButton,
                                children: texts.manage()
                            })
                        ]
                    }, integration.source)) : /*#__PURE__*/ _jsxs("div", {
                    className: styles.emptyStateWrapper,
                    "data-testid": "empty-state-container",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "bold",
                            children: texts.noAppsConnected()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "secondary",
                            children: texts.appsDescription()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'explore_apps'
                            },
                            size: "medium",
                            onClick: onExploreApps,
                            className: styles.exploreAppsButton,
                            testId: "explore-apps-button",
                            children: texts.exploreApps()
                        })
                    ]
                })
            })
        })
    });
}
